import axios from 'axios';
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';

import { ICountriesResponse, ICountry, ISubdivision, ISubdivisionsResponse } from '@models';

export interface IReferenceDataStore {
  countries?: ICountry[];
  /** Subdivisions per country, keyed by the UPPERCASE alpha-3 code. */
  subdivisionsByCountry: Record<string, ISubdivision[]>;
  fetchCountries: () => Promise<void>;
  fetchSubdivisions: (code3: string) => Promise<ISubdivision[]>;
}

// In-flight subdivision requests, so concurrent callers for one country share a single request
const pendingSubdivisions = new Map<string, Promise<ISubdivision[]>>();

/**
 * @file Reference Data Store
 *
 * Zustand store for the canonical country and state/province lists served by the
 * personnel service. Countries load once at startup; subdivisions load per country
 * on demand. Both are cached for the session.
 */
export const useReferenceDataStore = create<IReferenceDataStore>()(
  subscribeWithSelector(
    devtools(
      (set, get) => ({
        countries: undefined,
        subdivisionsByCountry: {},

        fetchCountries: async (): Promise<void> => {
          if (get().countries) {
            return;
          }

          try {
            const res = await axios.get(`${PERSONNEL_SERVICE_URL}/countries`);
            const { countries } = res.data as ICountriesResponse;
            set({ countries }, false, 'fetchCountries/success');
          } catch (error) {
            console.error('Error fetching countries:', error);
            throw error; // Re-throw to allow callers to handle the error
          }
        },

        fetchSubdivisions: (code3: string): Promise<ISubdivision[]> => {
          const key = code3.toUpperCase();

          const cached = get().subdivisionsByCountry[key];
          if (cached) {
            return Promise.resolve(cached);
          }

          if (pendingSubdivisions.has(key)) {
            return pendingSubdivisions.get(key);
          }

          const request = (async () => {
            try {
              const res = await axios.get(`${PERSONNEL_SERVICE_URL}/countries/${key}/subdivisions`);
              const { subdivisions } = res.data as ISubdivisionsResponse;
              set(
                (state) => ({ subdivisionsByCountry: { ...state.subdivisionsByCountry, [key]: subdivisions } }),
                false,
                'fetchSubdivisions/success',
              );
              return subdivisions;
            } catch (error) {
              console.error(`Error fetching subdivisions for ${key}:`, error);
              throw error; // Re-throw to allow callers to handle the error
            } finally {
              pendingSubdivisions.delete(key);
            }
          })();

          pendingSubdivisions.set(key, request);
          return request;
        },
      }),
      { name: 'ReferenceDataStore' },
    ),
  ),
);
